/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
/* eslint-disable complexity */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    type BaseRequestOptionsArgs,
    type HttpResponse,
    EventService,
    HttpService,
    I18nService,
    PermissionService,
    UtilService,
} from '@dm/core';
import { ToolService } from 'app/common/services/tool.service';
import { FeatureConfig } from 'app/config/feature';
import { SWITCH } from 'app/config/switch';
import { FilesystemSnapshotStrService } from 'app/feature/protection/object/filesystem/filesystem-snapshot/filesystem-snapshot-str.service';
import { HyperMetroPairConst } from 'app/feature/protection/object/lun/hyper-metro/hyper-metro-pair-str-service';
import { uniqBy as _uniqBy } from 'lodash';
import { type Observer, Observable, of, Subject, zip } from 'rxjs';
import { tap } from 'rxjs/operators';

import { searchHighHelpEn } from '../../../../assets/global-search/help-high/highHelpEn';
import { searchHighHelpZh } from '../../../../assets/global-search/help-high/highHelpZh';
import { searchMiddleHelpEn } from '../../../../assets/global-search/help-middle/middleHelpEn';
import { searchMiddleHelpZh } from '../../../../assets/global-search/help-middle/middleHelpZh';
import { environment } from '../../../../environments/environment';
import { type SearchParams } from './global-infigo/const';
import { HelpProvider } from './global-infigo/infigo.provider';
import { ConfigInsightStrService } from './global-search-config/config-insight/config-insight-str.service';
import { ConfigProtectionStrService } from './global-search-config/config-protection/config-protection-str.service';
import { ConfigServicesStrService } from './global-search-config/config-services/config-services-str.service';
import { ConfigSettingsStrService } from './global-search-config/config-settings/config-settings-str.service';
import { ConfigSystemStrService } from './global-search-config/config-system/config-system-str.service';
import {
    type DataConfig,
    type DataConfigOperation,
    type HelpResult,
    type SearchData,
    type SearchResult,
    DataTypeE,
    DataURL,
    GroupTypeE,
    HistoryTypeE,
} from './global-search-str.service';

@Injectable({ providedIn: 'root' })
export class GlobalSearchService {
    get = this.i18nService.get;
    zhResource = {};
    enResource = {};

    dataMap: { [key: string]: DataConfig };
    operationsData: SearchData[] = [];
    pagesData: SearchData[] = [];

    /** 快捷推荐显示内容 */
    defaultSearchData: { [key: string]: SearchData[] } = {};
    searchData: SearchData[] = [];
    historyData: {
        [HistoryTypeE.operation]?: SearchData[];
        [HistoryTypeE.page]?: SearchData[];
    } = {};

    updateSearchSource = new Subject();
    isOem = this.toolService.isOem();
    isProdRelease = Boolean(FeatureConfig.IsShowHighProdGlobalSearchShowList &&
        (environment.env === 'release' || environment.env === 'release_crack'));

    constructor(
        private i18nService: I18nService,
        private httpService: HttpService,
        private http: HttpClient,
        private utilService: UtilService,
        private toolService: ToolService,
        public permissionService: PermissionService,
        public fsSnapshotStr: FilesystemSnapshotStrService,
        public lunHyperStrServcie: HyperMetroPairConst,
        private eventService: EventService,
        private insightStr: ConfigInsightStrService,
        private protectionStr: ConfigProtectionStrService,
        private servicesStr: ConfigServicesStrService,
        private settingsStr: ConfigSettingsStrService,
        private systemStr: ConfigSystemStrService,
    ) {
    }

    initSearchData(): void {
        // 将global-search-config中的几种dataMap合并
        const mergedDataMap = {
            ...this.insightStr.getDataMap(),
            ...this.protectionStr.getDataMap(),
            ...this.servicesStr.getDataMap(),
            ...this.settingsStr.getDataMap(),
            ...this.systemStr.getDataMap(),
        };

        // 过滤hide成员
        Object.keys(mergedDataMap).forEach((key) => {
            if (mergedDataMap[key].hide) {
                delete mergedDataMap[key];
            }
        });

        this.dataMap = mergedDataMap;

        this.clearData();
        this.filterHiddenItemMap();
        this.getData(this.dataMap);
    }

    clearData(): void {
        this.operationsData = [];
        this.pagesData = [];
    }

    filterHiddenItemMap(): void {
        const data = { ...this.dataMap };

        // 删除hide的成员
        Object.keys(data).forEach((key: string) => {
            if (data[key].hide) {
                delete this.dataMap[key];
            }
        });
    }

    getData(dataConfig: { [key: string]: DataConfig }): void {
        Object.keys(dataConfig).forEach((key) => {
            const data: DataConfig = dataConfig[key];
            data.objectDes = this.getDescription(key, data);

            if (Object.prototype.hasOwnProperty.call(data, 'operation')) {
                data.operation.forEach((oper) => {
                    this.checkOperationShow(oper, data);
                });
            }

            this.checkPageShow(data);
        });

        this.handleSearchData();
        this.getDefaultShowData();
    }

    /**
     * 获取对象描述
     * @param key   dataMap对象的属性名
     * @param data  dataMap对象的属性值
     */
    getDescription(key: string, data: DataConfig): string {
        return (key === DataTypeE.recycleBin) ? 'home.search_term_inRecycleBin_lab' : data.page.searchKey;
    }

    /**
     * 判断对象“操作”的显示权限
     * @param operation  每个对象所拥有的operation项
     * @param data  dataMap对象的属性值
     */
    checkOperationShow(operation: DataConfigOperation, data: DataConfig): void {
        const license = (operation.license ? operation.license : data.license) as string[] | string[][];

        // 角色和用户创建时需要判断是否为超级管理员
        const isSuperAdmin = data.type === DataTypeE.user || data.type === DataTypeE.role ?
            this.permissionService.isSuperAdmin() :
            true;
        const isShow = this.permissionService.checkShow(operation.permission, license) &&
            (!data.isNASFeature || SWITCH.SUPPORT_NAS) &&
            (!data.isSmartGUIFeature || SWITCH.SUPPORT_SMART_GUI) &&
            isSuperAdmin &&
            (!operation.noOem || !this.isOem) &&
            (!operation.noProdRelease || !this.isProdRelease) &&
            !operation.notSupportFsHyperMetroDomainFeature &&
            !data.isNotSupportFeatureInOceanMicro &&
            !operation.noCreateStoragePoolFeature &&
            !operation.noRestartShutDownFeature &&
            !operation.noFcFeature &&
            !operation.noRoceFeature &&
            !operation.noBondFeature &&
            !operation.hide;

        if (isShow) {
            this.operationsData.push({
                searchKey: this.get(operation.searchKey),
                name: this.get(operation.searchKey),
                type: data.type,
                groupType: GroupTypeE.operation,
                url: data.url,
                tab: data.tab,
                subTab: data.subTab,
                action: operation.action,
                actionParams: operation.actionParams,
                defaultSearch: operation.defaultSearch,
                show: isShow,
            });
        }
    }

    /**
     * 判断对象“页面”的显示权限
     * @param data  dataMap对象的属性值
     */
    checkPageShow(data: DataConfig): void {
        const pageLicense = (data.page.license ? data.page.license : data.license) as string[] | string[][];
        const pageShow = this.permissionService.checkShow(data.page.permission, pageLicense) &&
            (!data.isNASFeature || SWITCH.SUPPORT_NAS) &&
            (!data.isSmartGUIFeature || SWITCH.SUPPORT_SMART_GUI) &&
            (!data.isContainerFeature || SWITCH.SUPPORT_CONTAINER) &&
            (!data.isFsHyperMetroFeature || FeatureConfig.IsShowProtectFSHyperMetro) &&
            (!data.page.noOem || !this.isOem) &&
            (!data.page.noProdRelease || !this.isProdRelease) &&
            !data.page.notSupportFsHyperMetroDomainFeature &&
            !data.page.noRoceFeature &&
            !data.page.noFcFeature &&
            !data.page.noBondFeature &&
            !data.isNotSupportFeatureInOceanMicro;

        if (pageShow) {
            this.pagesData.push({
                searchKey: this.get(data.page.searchKey),
                name: this.get(data.page.searchKey),
                type: data.type,
                groupType: GroupTypeE.page,
                url: data.url,
                tab: data.tab,
                subTab: data.subTab,
                defaultSearch: data.page.defaultSearch,
                defaultAdvancedSearch: data.page.defaultAdvancedSearch,
                advancedType: data.page.advancedType,
                show: pageShow,
            });
        }
    }

    /**
     * 处理搜索数据和搜索页默认展示数据
     */
    handleSearchData(): void {
        this.searchData = [...this.operationsData, ...this.pagesData];
        this.defaultSearchData.objects = [];
        this.defaultSearchData.operations = this.operationsData.filter((item: SearchData) => item.defaultSearch);
        this.defaultSearchData.pages = this.pagesData.filter((item: SearchData) => item.defaultSearch);
        this.defaultSearchData.advancedPages = this.pagesData.filter((item: SearchData) => item.defaultAdvancedSearch);
        this.updateSearchSource.next({
            operation: [...this.operationsData],
            page: [...this.pagesData],
        });
    }

    /**
     * 获取默认要显示的数据
     */
    getDefaultShowData(): void {
        this.defaultSearchData.operations = _uniqBy(
            [...this.getSearchHistory(HistoryTypeE.operation), ...this.defaultSearchData.operations],
            'searchKey',
        ).slice(0, 5);
        this.defaultSearchData.pages = _uniqBy(
            [...this.getSearchHistory(HistoryTypeE.page), ...this.defaultSearchData.pages],
            'searchKey',
        ).slice(0, 5);
        this.defaultSearchData.advancedPages = _uniqBy(
            [...this.getSearchHistory(HistoryTypeE.page), ...this.defaultSearchData.advancedPages],
            'searchKey',
        ).slice(0, 15);
    }

    /**
     * 获取历史操作记录
     */
    getSearchHistory(historyType: HistoryTypeE): SearchData[] {
        const history = this.historyData[historyType];

        return Array.isArray(history) ? history : [];
    }

    /**
     * 搜索结果高亮
     * @param content
     * @param highlightContent
     * @param choose
     */
    getHighLight(content: string, highlightContent: string, color: string, choose?: boolean): string {
        const hightLight = content.replace(new RegExp(`(${highlightContent})`, 'i'), `<strong><font color="${color}">$1</font></strong>`);

        return choose ? content : hightLight;
    }

    /**
     *  加载国际化文件
     *  @param url json文件地址
     */
    load(url: string): Observable<any> {
        return this.http.get(url);
    }

    /**
     *  初始化加载i18n资源
     */
    initI18n(): Observable<[{ key: string }, { key: string }]> {
        return zip(this.load('/assets/i18n/resource.zh.json'), this.load('/assets/i18n/resource.en.json')).pipe(
            tap((res) => {
                const [zh, en] = res;
                this.zhResource = zh;
                this.enResource = en;
                this.initSearchData();

                // License更新后重新设置search配置
                this.eventService.subscribe('license.update', () => {
                    this.clearHistory();
                    this.initSearchData();
                });
            }),
        );
    }

    /**
     *  查找operate和page的内容
     *  @param param 搜索value
     */
    findPageAndOperationHelp(oriParam: string): Observable<any> {
        let param = oriParam;
        param = param ? param.toLocaleLowerCase() : '';
        const result = param ? this.filterData(param) : this.searchData;
        const helps = this.findHelp(param);

        return of(result.concat(helps as any));
    }

    /**
     *  过滤满足条件的结果
     *  @param value 搜索value
     */
    filterData(value: string): SearchData[] {
        return this.searchData.filter((data) => data.searchKey.toLocaleLowerCase().includes(value));
    }

    /**
     *  查找联机帮助中的内容
     *  @param inputParam 用户输入的值
     */
    findHelp(inputParam: string): HelpResult[] {
        let source;
        if (FeatureConfig.IsHighProdHelp) {
            source = this.i18nService.language === 'zh' ? searchHighHelpZh : searchHighHelpEn;
        } else {
            source = this.i18nService.language === 'zh' ? searchMiddleHelpZh : searchMiddleHelpEn;
        }
        const provider = new HelpProvider(source);
        let helpResult = [];
        provider.search(inputParam).subscribe((result: any[]) => helpResult = result);
        helpResult.forEach((item) => item.groupType = 'help');

        return helpResult;
    }

    /**
     *  查找object的内容
     *  @param param 搜索value
     */
    findObject(param: string, range?: string): Observable<SearchData[]> {
        return new Observable((observer: Observer<SearchData[]>) => {
            if (param) {
                this.getSearchObj(param, range).subscribe(({ data }: { data: any[] }) => {
                    const backendResult = this.handleBackendResult(data);
                    observer.next(backendResult);
                    observer.complete();
                });
            } else {
                observer.next([]);
                observer.complete();
            }
        });
    }

    transform = (arr: any[] | { ID: string }, type: string | number, defaultType?: DataTypeE): SearchParams[] | SearchParams => {
        const query = (item): any => {
            const precisionType = type ? item[type] : defaultType;
            const itemData = this.dataMap[precisionType];
            const license = itemData.page.license as string[] | string[][];
            const show = this.permissionService.checkShow(itemData.page.permission, license) &&
                (!itemData.isNASFeature || SWITCH.SUPPORT_NAS);
            if (show) {
                return {
                    searchKey: item.name || item.NAME,
                    name: item.name || item.NAME,
                    url: itemData.url,
                    type: precisionType,
                    groupType: GroupTypeE.object,
                    isObject: true,
                    vstoreName: item.vstoreName,
                    vstoreId: item.vstoreId,
                    objectDes: this.get(itemData.objectDes),
                    action: 'searchDetail', // 约定方法名叫searchDetail 不可重名
                    actionParams: item.id || item.ID || item.sequence,
                    tab: itemData.tab,
                    subTab: itemData.subTab,
                    item,
                    show: true,
                };
            }

            return undefined;
        };
        if (Array.isArray(arr)) {
            return arr.map(query);
        }

        return query(arr);
    };

    /**
     *  查询对象
     *  @param name 对象名称
     *  @param range 范围
     *  @param options http请求选项
     */
    getSearchObj(name?: string, range?: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl(DataURL.object).setParams({
            name: `${name}`,
            range: range ? range : '[0-21]',
        });

        return this.httpService.get(rest, options);
    }

    /**
     *  处理后端结果
     *  @param data 搜索结果组成的数组
     */
    handleBackendResult(data: SearchResult[]): SearchData[] {
        const backendData = data;
        const result = [];
        backendData.forEach((item) => {
            const itemData = this.dataMap[item.precisionType];
            const license = itemData.page?.license as string[] | string[][];
            const isShow = this.permissionService.checkShow(itemData.page?.permission, license) &&
                (!itemData.isNASFeature || SWITCH.SUPPORT_NAS);

            if (isShow) {
                result.push({
                    searchKey: item.name,
                    name: item.name,
                    url: itemData.url,
                    type: item.precisionType,
                    groupType: GroupTypeE.object,
                    // 微存储屏蔽多租户
                    ...(!FeatureConfig.IsSupportVStoreService ? [] : [
                        {
                            vstoreName: item.vstoreName,
                            vstoreId: item.vstoreId,
                        },
                    ]),
                    isObject: true,
                    objectDes: this.get(itemData.objectDes),
                    action: 'searchDetail', // 约定方法名叫searchDetail 不可重名
                    actionParams: item.id,
                    tab: itemData.tab,
                    subTab: itemData.subTab,
                    show: true,
                });
            }
        });

        return result;
    }

    clearHistory(): void {
        this.historyData[HistoryTypeE.operation] = [];
        this.historyData[HistoryTypeE.page] = [];
    }

    /**
     *  格式化内容带括号
     */
    getBracketMsg = (str: string): string => this.get('common.brackets_connection_lab', [str]);

    /**
     *  判断是否为json
     */
    checkIsJSON(parame: string): boolean {
        if (typeof parame === 'string') {
            try {
                const obj = JSON.parse(parame);
                if (typeof obj === 'object' && obj) {
                    return true;
                } else {
                    return false;
                }
            } catch (e) {
                return false;
            }
        }

        return false;
    }
}
